/**
 * How the Message Log writes an instant.
 *
 * The column used to show the time of day with milliseconds and nothing else,
 * which is exactly right for the last hour and wrong for everything older: a
 * log spanning days made the reader hover every row to learn which day it was.
 * Chosen on 21 September: the day in words when it is close, the date when it
 * is not, and the time to the second — `Today 19:36:41`, `Yesterday 23:15:50`,
 * `18 Sep 14:02:33`, and the year added once it is not this one. Milliseconds
 * move to the hover card, where somebody lining a row up against a provider's
 * log reaches for them.
 *
 * The drawer's timeline keeps `timeOfDay` with milliseconds: between two
 * events of one message the day never changes and the milliseconds are the
 * point.
 */

/** Which clock the log reads in. A viewer's choice, remembered per person. */
export type LogZone = "local" | "utc";

interface Parts {
  year: string;
  month: string;
  monthLong: string;
  day: string;
  weekday: string;
  hour: string;
  minute: string;
  second: string;
}

// en-US for the names, whatever the viewer's locale: en-GB abbreviates
// September as "Sept", and one four-letter month throws the column's alignment
// out for a month of the year. 24-hour, because a log read across time zones
// has no use for a.m.
function partsOf(at: Date, timeZone: string | undefined): Parts {
  const format = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "short",
    day: "numeric",
    weekday: "long",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const parts = Object.fromEntries(format.formatToParts(at).map((p) => [p.type, p.value]));
  const monthLong = new Intl.DateTimeFormat("en-US", { timeZone, month: "long" }).format(at);
  return {
    year: parts.year ?? "",
    month: parts.month ?? "",
    monthLong,
    day: parts.day ?? "",
    weekday: parts.weekday ?? "",
    hour: parts.hour ?? "",
    minute: parts.minute ?? "",
    second: parts.second ?? "",
  };
}

function timeZoneOf(zone: LogZone): string | undefined {
  return zone === "utc" ? "UTC" : undefined;
}

/** The calendar day an instant falls on in a zone, as YYYY-MM-DD. */
function dayKey(at: Date, timeZone: string | undefined): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(at);
}

/**
 * The day before a YYYY-MM-DD, by calendar rather than by subtracting a day of
 * milliseconds. Twenty-four hours before half past midnight the morning after a
 * spring-forward day is two calendar days back, and "Yesterday" would be wrong
 * on exactly those mornings.
 */
function dayBefore(key: string): string {
  const [year, month, day] = key.split("-").map(Number);
  const before = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (day ?? 1) - 1));
  return before.toISOString().slice(0, 10);
}

/** `Today 19:36:41`, `Yesterday 23:15:50`, `18 Sep 14:02:33`, `18 Sep 2025 14:02:33`. */
export function logStamp(at: Date, zone: LogZone, now: Date = new Date()): string {
  const timeZone = timeZoneOf(zone);
  const p = partsOf(at, timeZone);
  const time = `${p.hour}:${p.minute}:${p.second}`;

  const today = dayKey(now, timeZone);
  const day = dayKey(at, timeZone);
  if (day === today) return `Today ${time}`;
  if (day === dayBefore(today)) return `Yesterday ${time}`;
  if (p.year === partsOf(now, timeZone).year) return `${p.day} ${p.month} ${time}`;
  return `${p.day} ${p.month} ${p.year} ${time}`;
}

/**
 * The viewer's own zone as their locale abbreviates it: CEST, EDT, or GMT+2
 * where the locale has no abbreviation for it.
 */
export function localZoneName(at: Date = new Date()): string {
  const part = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
    .formatToParts(at)
    .find((p) => p.type === "timeZoneName");
  return part?.value ?? "local";
}

export function zoneLabel(zone: LogZone, at: Date = new Date()): string {
  return zone === "utc" ? "UTC" : localZoneName(at);
}

function clock(at: Date, zone: LogZone): string {
  const p = partsOf(at, timeZoneOf(zone));
  // Milliseconds are the same in every zone.
  const ms = String(at.getUTCMilliseconds()).padStart(3, "0");
  return `${p.hour}:${p.minute}:${p.second}.${ms}`;
}

const relativeFormat = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

/** "44 minutes ago", "yesterday", "3 days ago". */
export function relativeTo(at: Date, now: Date = new Date()): string {
  const minutes = Math.round((at.getTime() - now.getTime()) / 60_000);
  if (Math.abs(minutes) < 1) return "just now";
  if (Math.abs(minutes) < 60) return relativeFormat.format(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return relativeFormat.format(hours, "hour");
  return relativeFormat.format(Math.round(hours / 24), "day");
}

/** What the hover card says: the day, both clocks with milliseconds, how long ago. */
export interface StampDetail {
  day: string;
  lines: Array<{ time: string; zone: string }>;
  relative: string;
}

export function stampDetail(at: Date, zone: LogZone, now: Date = new Date()): StampDetail {
  const p = partsOf(at, timeZoneOf(zone));
  const local = { time: clock(at, "local"), zone: localZoneName(at) };
  const utc = { time: clock(at, "utc"), zone: "UTC" };
  return {
    day: `${p.weekday} ${p.day} ${p.monthLong} ${p.year}`,
    // The zone the column is read in comes first.
    lines: zone === "utc" ? [utc, local] : [local, utc],
    relative: relativeTo(at, now),
  };
}

/** One sentence for a screen reader, which cannot hover. */
export function stampSentence(at: Date, zone: LogZone, now: Date = new Date()): string {
  const detail = stampDetail(at, zone, now);
  const [first, second] = detail.lines;
  return `${detail.day}, ${first?.time} ${first?.zone}, ${second?.time} ${second?.zone}, ${detail.relative}`;
}
